/**
 * The step graph, and the single source of truth for it.
 *
 * Everything below derives from this list — the step union, the row count the
 * db package inserts, the gate the API's unlock cascade checks. Adding a step
 * means editing this array and `ONBOARDING_STEP_COPY`; nothing else carries a
 * step number as a literal.
 */
export const ONBOARDING_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
/** Rows in one onboarding run. */
export const ONBOARDING_STEP_COUNT = ONBOARDING_STEPS.length;
/** The step whose completion unlocks the dashboard. Auto-completed by the API workflow. */
export const ONBOARDING_UNLOCK_STEP = 5;
/** Last step. Auto-completed when the launch checklist closes. */
export const ONBOARDING_FINAL_STEP = ONBOARDING_STEPS[ONBOARDING_STEPS.length - 1];
/**
 * Launch Day. Gated on the founder's launch date, so it is activated on its own.
 *
 * The step the founder closes themselves, by pressing "Next" on the Launch Day
 * screen once the countdown to their first day with their Doer has run out. The
 * date is the server's to check (see `lib/launch-day.ts`), which is why this
 * step is NOT on {@link FOUNDER_COMPLETABLE_STEPS} and has an endpoint of its
 * own instead.
 */
export const ONBOARDING_LAUNCH_STEP = 10;
/**
 * The Founder Foundations course. Closed by an evaluator, not by a founder CTA:
 * the step is done when every lesson of the designated course
 * (`content_courses.is_foundations`) is watched past
 * `FOUNDATIONS_LESSON_COMPLETE_PERCENT`.
 *
 * Recommended, not gating. It opens alongside 7/8/9 and none of them waits on
 * it — a founder can brief a playbook and book a coaching call with the course
 * untouched.
 */
export const ONBOARDING_FOUNDATIONS_STEP = 6;
/**
 * Brief the first playbook. Closed by the submission path, not by a founder CTA.
 *
 * While it is open the founder sees only the starter playbooks (see
 * `ONBOARDING_STARTER_PLAYBOOK_COUNT`); completing it reveals the full library.
 */
export const ONBOARDING_FIRST_PLAYBOOK_STEP = 7;
/**
 * How many of a founder's recommended playbooks are "starters" — the short list
 * /playbooks shows while step 7 is open.
 *
 * Taken as the first N of `recommended_playbooks` by `display_order` rather than
 * a flag on the row. Every roadmap seeds the same three Founder Strategy
 * playbooks at orders 1–3 (Craft Your Market Message, Build Your Brand Identity,
 * and the category's offer playbook), so the head of the roadmap already IS the
 * starter set — and the admin reorder endpoint renumbers a category 1..N with no
 * gaps, which keeps that true. A stored `is_starter` would instead survive a
 * reorder and strand "starters" in the middle of the roadmap, showing a founder
 * three playbooks out of order with nothing explaining why.
 */
export const ONBOARDING_STARTER_PLAYBOOK_COUNT = 3;
/** Steps 1–4: all must close before the unlock fires. Exactly one is active at a time. */
export const ONBOARDING_GATE_STEPS = ONBOARDING_STEPS.filter((step) => step < ONBOARDING_UNLOCK_STEP);
/**
 * Steps 6–10: opened together by the unlock. Only 11 is derived.
 *
 * Step 10 is in this set, which reverses what the Developer Brief says. The
 * Brief has it "greyed out until the actual launch date"; Mahala overrode that
 * in Slack on 2026-08-07 — the launch date gates COMPLETION, not visibility. The
 * Launch Day screen is therefore reachable from the unlock: it is where the
 * founder watches the countdown to their first day and works through their
 * Founder Strategy Playbooks while they wait. The date rule lives in the step-10
 * completion guard, not here.
 */
export const ONBOARDING_POST_UNLOCK_STEPS = ONBOARDING_STEPS.filter((step) => step > ONBOARDING_UNLOCK_STEP && step !== ONBOARDING_FINAL_STEP);
/** Steps the workflow closes on its own; no caller may complete them directly. */
export const ONBOARDING_DERIVED_STEPS = [
    ONBOARDING_UNLOCK_STEP,
    ONBOARDING_FINAL_STEP,
];
/**
 * Sign the service agreement. DocuSeal is where the founder signs, but the
 * signature applies a Keap tag and Keap's `founder.agreement_signed` event is
 * the only thing that closes this step — nothing the founder does on our own
 * signing surface completes it (decision #1).
 */
export const ONBOARDING_AGREEMENT_STEP = 1;
/**
 * The Business Brain intake. Closed by `recordBriefAnswer` when the last
 * required question lands, never by the founder — the step is off the
 * founder-completable allowlist so nobody can POST their way to an unlocked
 * dashboard. Named because finalize-time work keys its idempotency markers to
 * this step's `metadata`.
 */
export const ONBOARDING_INTAKE_STEP = 2;
/**
 * Book the kick-off call. Closed by Calendly's `invitee.created` webhook, never
 * by the founder — a booking is a fact only Calendly can observe.
 */
export const ONBOARDING_KICKOFF_STEP = 3;
/** Book the first coaching call. Same webhook, a different Calendly event type. */
export const ONBOARDING_COACHING_STEP = 8;
/**
 * Meet your Doer. Closed by the founder's own confirmation that they watched
 * the introduction, on `/doer-profile` — so it is on the founder-completable
 * allowlist, and its `metadata` carries which doers that confirmation covered
 * ({@link OnboardingDoerIntroConfirmation}).
 */
export const ONBOARDING_MEET_DOER_STEP = 9;
/**
 * Steps closed by an integration the founder never sees — DocuSeal/Keap for the
 * agreement, Calendly for the two bookings.
 *
 * These are the steps that can strand a founder through no fault of their own: a
 * webhook that never arrived, or one whose handler failed, leaves the tracker
 * waiting forever on an event that has already happened in the outside world.
 * The admin unblock surface (Onboarding 12) is scoped to exactly this list.
 */
export const ONBOARDING_WEBHOOK_STEPS = [
    ONBOARDING_AGREEMENT_STEP,
    ONBOARDING_KICKOFF_STEP,
    ONBOARDING_COACHING_STEP,
];
export function isOnboardingWebhookStep(step) {
    return ONBOARDING_WEBHOOK_STEPS.includes(step);
}
/**
 * The only steps a founder may complete from the client.
 *
 *   4 — the product tour is finished or skipped, which only the founder knows
 *   9 — the founder confirms they watched their doer's intro video
 *
 * Everything else is asserted by something that can actually observe it: steps
 * 1/3/8 by webhooks, step 2 by the intake finalize service, steps 5/11 by the
 * application workflow, step 10 by the launch-date guard. The API 403s anything
 * outside this list — see `isFounderCompletableStep`.
 *
 * Step 10 stays OFF this list even though the founder is the one who presses
 * "Next" on the Launch Day screen. Its condition — that the launch date has
 * arrived — is a fact only the server can check, so a founder-completable step
 * 10 would let a founder close it early by POSTing. It has its own endpoint
 * (`POST /v1/onboarding/launch-day/complete`) which checks the date first.
 */
export const FOUNDER_COMPLETABLE_STEPS = [4, 9];
export function isFounderCompletableStep(step) {
    return FOUNDER_COMPLETABLE_STEPS.includes(step);
}
/**
 * The booked call's start time, or null when the step was closed some other way
 * (an admin override carries no booking) or the payload had no start time.
 *
 * Takes `metadata` rather than the whole step so callers can't accidentally read
 * a date off a step that isn't complete.
 */
export function readCalendlyScheduledAt(metadata) {
    const value = metadata?.scheduled_at;
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
}
/**
 * The stored submission id, or null when nothing has been stored yet — a
 * founder who signed from the emailed DocuSeal link never touched our endpoint,
 * so step 1 can close with no submission on record.
 */
export function readAgreementSubmissionId(metadata) {
    const value = metadata?.docuseal_submission_id;
    return typeof value === 'number' && Number.isInteger(value) ? value : null;
}
/**
 * The confirm token a reset requires in its body, and the word the admin dialog
 * makes them type. Not a checksum of anything — its job is to make the call
 * impossible to issue by accident (a mistyped path, a body replayed from another
 * endpoint), which no combination of the founder's own identifiers can do, since
 * the caller already has all of those.
 */
export const ONBOARDING_RESET_CONFIRM = 'RESET';
/**
 * How short an override reason may be. Shared so the dialog's confirm button
 * enables on exactly the input the API accepts — a client threshold that drifted
 * below the schema's would light the button up and then 400.
 */
export const ONBOARDING_OVERRIDE_REASON_MIN = 3;
/**
 * The booking section of the Kick-Off Call page — the id on the element every
 * CTA on that page already scrolls to, named here because step 3's CTA arrives
 * from elsewhere and has to ask for it by name.
 */
export const ONBOARDING_KICKOFF_BOOKING_ANCHOR = 'book-kick-off-call';
/**
 * The calendar on the Coaching Calls page — step 8's equivalent of the kick-off
 * anchor, and there for the same two reasons.
 *
 * The page leads with the live-coaching pitch and the coaches, so a founder sent
 * to the top of it has been shown everything except the thing they were asked to
 * do. And it is what the panel's CTA re-scrolls to when it is pressed from
 * ALREADY on the page: a navigation to the location you occupy fires nothing, so
 * without an anchor to aim at that press is dead.
 */
export const ONBOARDING_COACHING_BOOKING_ANCHOR = 'book-coaching-call';
/** Canonical founder-facing copy for the eleven steps and their notifications. */
export const ONBOARDING_STEP_COPY = {
    1: {
        title: 'Sign your agreement',
        shortTitle: 'Service Agreement',
        description: 'Review and sign your Doneverse agreement to get started.',
        estimatedMinutes: 5,
        cta: 'Sign agreement',
        // No route: the document is signed on a full-screen surface over the shell,
        // which a founder on step 1 has nothing else to do with. See
        // `OnboardingStepSurface`.
        route: null,
        surface: 'agreement',
    },
    2: {
        title: 'Business Brain intake',
        shortTitle: 'Business Brain',
        description: 'Tell us about your business so your doer and Coach Grace AI know it as well as you do.',
        estimatedMinutes: 20,
        cta: 'Start intake',
        // The conversational intake, NOT `/business-brief`. The two are different
        // artifacts and both survive: `/business-brief` is the durable brief a
        // founder edits forever and their doer reads before every playbook, while
        // this is the one-shot 41-question capture that produces it (the question
        // registry lives in validation/intake-questions.ts). Pointing step 2 at the
        // brief page would ask a founder to fill in by hand exactly what the
        // conversation is there to collect.
        route: '/founder-intake',
    },
    3: {
        title: 'Book your kick-off call',
        shortTitle: 'Kick-Off Call',
        description: 'Pick a time to meet your team and set your first priorities.',
        cta: 'Book kick-off call',
        // The existing Kick-Off Call page, which onboarding owns while step 3 is
        // open. Deliberately NOT gated on the `kick-off-call` flag: that flag hides
        // the sidebar entry from founders who are browsing, and a locked founder on
        // step 3 is not browsing — the page is the only way past the overlay.
        route: '/kick-off-call',
        // Landing at the top of that page shows a founder the sales pitch for a call
        // they have already been told to book, with the booking widget a scroll
        // away. The page's own CTAs all scroll to the booking section; this one
        // arrives at it.
        hash: ONBOARDING_KICKOFF_BOOKING_ANCHOR,
    },
    4: {
        title: 'Take the product tour',
        shortTitle: 'Product Tour',
        description: 'A two-minute walk through the parts of Doneverse you will use every week.',
        estimatedMinutes: 2,
        cta: 'Start the tour',
        // No route: the tour spotlights the shell, so it opens over it rather than
        // navigating anywhere. See `OnboardingStepSurface`.
        route: null,
        surface: 'product-tour',
    },
    5: {
        title: 'Dashboard unlocked',
        // Never drawn: the tracker excludes step 5 from the rail (it is the unlock
        // boundary, not a task). Present because the copy record is total.
        shortTitle: 'Unlocked',
        description: 'Your workspace is open.',
        cta: 'Explore your dashboard',
        route: '/',
    },
    6: {
        title: 'Foundations course',
        shortTitle: 'Foundations',
        description: 'The short course that makes everything else land faster.',
        // No estimate: the course's real length is the sum of its lessons, which
        // lives in `content_courses` and changes when the course does.
        cta: 'Open the course',
        // Its own surface, not the Masterclass Vault: the vault is free-roam
        // browsing and this course is walked in order with watch tracking.
        // The course has no rollout flag of its own — the route guard gates
        // `/foundations` on `founder-onboarding`, so it opens and closes with the
        // tracker that sends founders to it.
        route: '/foundations',
    },
    7: {
        // "Doing Playbook" is the product's own name for the thing, and the step is
        // the founder's first one — the shorter title read as an internal noun
        // (ClickUp 86d40tryj). `shortTitle` stays short: it labels a rail node.
        title: 'Start your first Doing Playbook',
        shortTitle: 'First Playbook',
        description: 'Brief your first playbook and send it to your Doer.',
        estimatedMinutes: 10,
        cta: 'Choose a playbook',
        route: '/playbooks',
    },
    8: {
        title: 'Book your first coaching call',
        shortTitle: 'Coaching Call',
        description: 'Get your roadmap reviewed by a coach.',
        cta: 'Book coaching call',
        // The coaching calendar, where the founder picks a call and books it in the
        // embed that page already carries — same Calendly event type this step is
        // configured for, so the same webhook closes it. Deliberately NOT gated on
        // `coaching-call-recordings`: that flag hides the sidebar entry, and this
        // step is mandatory for every founder in the flow.
        route: '/coaching-calls',
        // Past the pitch and the coaches, at the calendar. Same reasoning as step
        // 3: the booking sits most of a screen down a long page.
        hash: ONBOARDING_COACHING_BOOKING_ANCHOR,
    },
    9: {
        title: 'Meet your doer',
        shortTitle: 'Meet Your Doer',
        description: "Watch your doer's intro video and say hello.",
        estimatedMinutes: 5,
        cta: 'Watch the intro',
        // The doer profile page, which leads with the intro video and carries the
        // confirmation checkbox — so the CTA now opens what it promises, and the
        // step is no longer closed by a button the founder presses instead of
        // watching anything. (Same beat as step 4 when the product tour landed.)
        //
        // Not flag-gated in the route guard (see FLAGGED_ROUTE_PREFIXES): the doer
        // is the page's other audience — it is where they write their own profile —
        // and a doer never carries their founder's `founder-onboarding` flag.
        //
        // The onboarding lock cannot route a founder here: step 9 only activates as
        // part of the post-unlock cascade (ONBOARDING_POST_UNLOCK_STEPS), and
        // `selectOnboardingLock` returns UNLOCKED the moment `state.unlocked` is
        // true. So this route never needs a lock exception.
        route: '/doer-profile',
    },
    10: {
        // "Launch Day", not "Launch checklist" (ClickUp 86d40tuc8). The step is no
        // longer a list of things to tick: it is the wait for the founder's first
        // day with their Doer, and the screen is a countdown to it. `shortTitle`
        // stays "Launch" — Gen kept that for the rail node.
        title: 'Launch Day',
        shortTitle: 'Launch',
        description: 'Count down to your first day with your Doer.',
        cta: 'Open Launch Day',
        // Open from the moment the dashboard unlocks, NOT from the launch date —
        // the date gates completion only (Mahala, 2026-08-07). So this is a real
        // destination the whole time the step is active, and the tracker's
        // "we'll move this along" waiting state no longer applies to step 10.
        //
        // Founder-only, unlike the `/launch-checklist` page it replaced: a doer has
        // no launch day of their own to count down to. Which is what lets the route
        // guard gate it on `founder-onboarding` (see FLAGGED_ROUTE_PREFIXES) where
        // the checklist could not — there is no second audience to bounce. Gating a
        // step's surface is normally the hazard this record's `route` doc warns
        // about, but not here: the flag being gated on is the same one that draws
        // the tracker, so nobody who can be sent here can be turned away.
        route: '/launch-day',
    },
    11: {
        title: 'Congratulations',
        shortTitle: 'Done',
        description: 'You are fully onboarded. Time to build.',
        cta: 'See your journey',
        route: null,
    },
};
//# sourceMappingURL=onboarding.js.map